/**
 * MODULE: useAssistantQuotaBanner.ts
 * PURPOSE: Combine 429 session flag with polled quota status for assistant banner
 * RECENT: 2026-07-08 [2.2.1554] — Clear stale session exceeded flag when quota API reports usage below limit.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  clearAssistantQuotaExceeded,
  dismissAssistantQuotaWarning,
  isAssistantQuotaWarningDismissed,
  type AssistantQuotaSeverity,
} from '../../components/assistant/assistantQuotaGuidance';
import { useAssistantQuotaStatus } from './useAssistantQuotaStatus';

export type AssistantQuotaBannerState = {
  visible: boolean;
  severity: AssistantQuotaSeverity;
  percentUsed?: number;
  dismiss: () => void;
  /** Blocks composer/send when quota is exhausted (API or live 429 session). */
  isExceeded: boolean;
};

export function useAssistantQuotaBanner(
  isActive: boolean,
  sessionExceeded: boolean,
  onSessionExceededRecovered?: () => void,
): AssistantQuotaBannerState {
  const { data } = useAssistantQuotaStatus(isActive);
  const [dismissedThreshold, setDismissedThreshold] = useState<number | null>(null);

  const apiStatus = data?.ai_tokens?.status;
  const apiSaysNotExceeded =
    apiStatus === 'ok' || apiStatus === 'warning' || apiStatus === 'critical';

  useEffect(() => {
    if (!isActive || !sessionExceeded || !apiSaysNotExceeded) return;
    clearAssistantQuotaExceeded();
    onSessionExceededRecovered?.();
  }, [isActive, sessionExceeded, apiSaysNotExceeded, onSessionExceededRecovered]);

  const effectiveSessionExceeded = sessionExceeded && !apiSaysNotExceeded;

  return useMemo(() => {
    const status = data?.ai_tokens?.status ?? 'ok';
    const percentUsed = data?.ai_tokens?.percent_used;
    const alertThreshold = data?.ai_tokens?.active_alert_threshold;

    if (effectiveSessionExceeded || status === 'exceeded') {
      return {
        visible: true,
        severity: 'exceeded' as const,
        percentUsed,
        isExceeded: true,
        dismiss: () => {
          clearAssistantQuotaExceeded();
          onSessionExceededRecovered?.();
        },
      };
    }

    let severity: AssistantQuotaSeverity | null = null;
    if (status === 'critical') severity = 'critical';
    else if (status === 'warning') severity = 'warning';

    if (!severity) {
      return {
        visible: false,
        severity: 'warning' as const,
        isExceeded: false,
        dismiss: () => undefined,
      };
    }

    const dismissKey = alertThreshold ?? (severity === 'critical' ? 90 : 75);
    if (dismissedThreshold === dismissKey || isAssistantQuotaWarningDismissed(dismissKey)) {
      return {
        visible: false,
        severity,
        percentUsed,
        isExceeded: false,
        dismiss: () => undefined,
      };
    }

    return {
      visible: true,
      severity,
      percentUsed,
      isExceeded: false,
      dismiss: () => {
        dismissAssistantQuotaWarning(dismissKey);
        setDismissedThreshold(dismissKey);
      },
    };
  }, [data, effectiveSessionExceeded, dismissedThreshold, onSessionExceededRecovered]);
}